/**
 * Naam-Grendel (Zero-Knowledge) van de Ziekmeldings-Wizard.
 *
 * Stroom A — de echte naam en het contactgegeven blijven uitsluitend in de
 *            browser van deze werkgever: AES-GCM-versleuteld in localStorage,
 *            met een per bedrijf gegenereerde, niet-exporteerbare sleutel in
 *            IndexedDB (LocalSecureStorage).
 * Stroom B — SHA-256(employer_id | contact) is de enige identifier die naar
 *            de server gaat; het pseudoniem MDW-xxxxx-X volgt daar
 *            deterministisch uit (zelfde afleiding als de backend).
 *
 * Werkt alleen in een secure context (https of localhost); daarbuiten is
 * crypto.subtle niet beschikbaar en wordt er niets lokaal bewaard.
 */

import { useCallback, useEffect, useState } from 'react';

export interface LokaleMedewerker {
  naam: string;
  contact: string;
  bewaard_op: string;
}

const DB_NAAM = 'belagom-naamgrendel';
const STORE = 'sleutels';
const opslagSleutel = (employerId: string) => `belagom:naamgrendel:${employerId}`;

export const naamGrendelBeschikbaar = () => typeof crypto !== 'undefined' && !!crypto.subtle && typeof indexedDB !== 'undefined';

const hex = (buf: ArrayBuffer) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
const b64 = (buf: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** Normalisatie vóór het hashen: e-mail lowercase, mobiel alleen cijfers/+ (06… → +316…). */
export function normaliseerContact(contact: string): string {
  const s = contact.trim();
  if (s.includes('@')) return s.toLowerCase();
  const raw = s.replace(/[\s\-().]/g, '');
  const nl = raw.match(/^(?:\+31|0031|31|0)(6\d{8})$/);
  if (nl) return `+31${nl[1]}`;
  return raw.replace(/^00/, '+');
}

/** Stroom B: SHA-256(employer_id | genormaliseerd contact), als hex. */
export async function medewerkerHash(employerId: string, contact: string): Promise<string> {
  const data = new TextEncoder().encode(`${employerId}|${normaliseerContact(contact)}`);
  return hex(await crypto.subtle.digest('SHA-256', data));
}

/** Zelfde afleiding als poortwachterKlokService.pseudoniemUitHash op de server. */
export function pseudoniemUitHash(hash: string): string {
  const schoon = hash.toLowerCase().replace(/[^0-9a-f]/g, '');
  const cijfers = parseInt(schoon.slice(0, 8), 16) % 100000;
  const letter = String.fromCharCode(65 + (parseInt(schoon.slice(8, 10), 16) % 26));
  return `MDW-${String(cijfers).padStart(5, '0')}-${letter}`;
}

// ─── LocalSecureStorage ───────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAAM, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idb<T>(db: IDBDatabase, modus: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, modus);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** De bedrijfscryptie-sleutel: niet-exporteerbaar, alleen in deze browser. */
async function bedrijfsSleutel(employerId: string): Promise<CryptoKey> {
  const db = await openDb();
  try {
    const bestaand = await idb<CryptoKey | undefined>(db, 'readonly', (s) => s.get(employerId));
    if (bestaand) return bestaand;
    const sleutel = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await idb(db, 'readwrite', (s) => s.put(sleutel, employerId));
    return sleutel;
  } finally {
    db.close();
  }
}

async function versleutel(sleutel: CryptoKey, tekst: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, sleutel, new TextEncoder().encode(tekst));
  return `${b64(iv)}.${b64(cipher)}`;
}

async function ontsleutel(sleutel: CryptoKey, blob: string): Promise<string> {
  const [iv, cipher] = blob.split('.');
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, sleutel, unb64(cipher));
  return new TextDecoder().decode(plain);
}

export type NaamRegister = Record<string, LokaleMedewerker>;

export async function leesNamen(employerId: string): Promise<NaamRegister> {
  if (!naamGrendelBeschikbaar()) return {};
  try {
    const blob = localStorage.getItem(opslagSleutel(employerId));
    if (!blob) return {};
    return JSON.parse(await ontsleutel(await bedrijfsSleutel(employerId), blob)) as NaamRegister;
  } catch {
    // Andere browser/sleutel weg: de namen zijn onherstelbaar — bewust (Zero-Knowledge)
    return {};
  }
}

export async function bewaarNaam(employerId: string, hash: string, m: Omit<LokaleMedewerker, 'bewaard_op'>): Promise<NaamRegister> {
  if (!naamGrendelBeschikbaar()) throw new Error('Lokale naamopslag vereist een beveiligde verbinding (https).');
  const register = await leesNamen(employerId);
  register[hash] = { ...m, bewaard_op: new Date().toISOString() };
  localStorage.setItem(opslagSleutel(employerId), await versleutel(await bedrijfsSleutel(employerId), JSON.stringify(register)));
  return register;
}

export async function verwijderNaam(employerId: string, hash: string): Promise<NaamRegister> {
  const register = await leesNamen(employerId);
  delete register[hash];
  if (naamGrendelBeschikbaar()) {
    localStorage.setItem(opslagSleutel(employerId), await versleutel(await bedrijfsSleutel(employerId), JSON.stringify(register)));
  }
  return register;
}

/** React-hook: het lokale naamregister van deze werkgever + bewaren/verwijderen. */
export function useNaamGrendel(employerId: string | null | undefined) {
  const [namen, setNamen] = useState<NaamRegister>({});
  const [geladen, setGeladen] = useState(false);
  useEffect(() => {
    let actief = true;
    setGeladen(false);
    if (!employerId) return;
    leesNamen(employerId).then((r) => {
      if (actief) {
        setNamen(r);
        setGeladen(true);
      }
    });
    return () => {
      actief = false;
    };
  }, [employerId]);
  const bewaar = useCallback(
    async (hash: string, m: Omit<LokaleMedewerker, 'bewaard_op'>) => {
      if (!employerId) return;
      setNamen(await bewaarNaam(employerId, hash, m));
    },
    [employerId]
  );
  const verwijder = useCallback(
    async (hash: string) => {
      if (!employerId) return;
      setNamen(await verwijderNaam(employerId, hash));
    },
    [employerId]
  );
  return { namen, geladen, bewaar, verwijder, beschikbaar: naamGrendelBeschikbaar() };
}
